import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../useApi";
import type {
  ControlState,
  DerivedMetrics,
  DetectedMilestone,
  Envelope,
  Evaluation,
  MarkerDiscrepanciesData,
  MilestoneConfidenceData,
  Recipe,
  RoastDetail as RoastDetailT,
  Timeseries,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { StarRating } from "../components/StarRating";
import { RoastChart } from "../components/RoastChart";
import { VerdictCard } from "../components/VerdictCard";
import { MarkerProvenance } from "../components/MarkerProvenance";
import { milestoneName, sourceLabel } from "../chart";
import {
  formatConfidence,
  formatDate,
  formatNumber,
  formatPercent,
  formatTemp,
  formatTime,
} from "../format";

// The full-resolution native store is 2 Hz, so a 12-minute roast is ~1400
// samples; 1200 keeps every milestone pinned (the handler pins them) and
// draws the curve without visible stair-stepping. The handler's ceiling
// is 2000.
const CHART_MAX_POINTS = 1200;

export function RoastDetail() {
  const { roastId = "" } = useParams();
  const api = useApi();
  const [roast, setRoast] = useState<RoastDetailT | null>(null);
  const [ts, setTs] = useState<Timeseries | null>(null);
  const [milestones, setMilestones] = useState<DetectedMilestone[] | null>(null);
  const [err, setErr] = useState<unknown>(null);

  // The verdict and the provenance checks load separately: a failure in
  // one of them is a missing card, not a missing page.
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [evalErr, setEvalErr] = useState<unknown>(null);
  const [discrepancies, setDiscrepancies] =
    useState<Envelope<MarkerDiscrepanciesData> | null>(null);
  const [lowConfidence, setLowConfidence] =
    useState<Envelope<MilestoneConfidenceData> | null>(null);
  const [provErr, setProvErr] = useState<unknown>(null);

  useEffect(() => {
    setErr(null);
    setRoast(null);
    setTs(null);
    setMilestones(null);
    setEvaluation(null);
    setEvalErr(null);
    setDiscrepancies(null);
    setLowConfidence(null);
    setProvErr(null);

    Promise.all([
      api.getRoast(roastId),
      api.getTimeseries(roastId, { max_points: CHART_MAX_POINTS }),
      api.getDetectedMilestones(roastId),
    ])
      .then(([r, t, m]) => {
        setRoast(r);
        setTs(t);
        setMilestones(m.milestones);
      })
      .catch(setErr);

    api.evaluateRoast(roastId).then(setEvaluation).catch(setEvalErr);

    Promise.all([
      api.findMarkerDiscrepancies(roastId),
      api.reviewMilestoneConfidence(roastId),
    ])
      .then(([d, l]) => {
        setDiscrepancies(d);
        setLowConfidence(l);
      })
      .catch(setProvErr);
  }, [api, roastId]);

  if (err) return <ErrorBox error={err} />;
  if (!roast || !ts || !milestones) return <p className="muted">Loading…</p>;

  // Series are stored in °C whatever the export said; `source_temp_unit`
  // is what the file used. We display in °C — the stored unit — and say
  // so when the source differed.
  const unit: "C" | "F" = roast.temp_unit ?? "C";
  const isRated = (roast.rating?.stars ?? 0) > 0;
  const title = roast.coffee_label ?? roast.coffee_id ?? roast.name ?? roast.roast_id;

  return (
    <div className="col">
      <div>
        <Link to="/">← Roasts</Link>
      </div>

      <div className="card">
        <div className="row">
          <div>
            <h1 style={{ margin: "0 0 4px 0" }}>{title}</h1>
            <div className="muted small mono">{roast.roast_id}</div>
            <div className="muted small">
              {formatDate(roast.roast_date)} · {roast.batch_size_g ?? "?"}g
              {roast.operator && ` · operator ${roast.operator}`}
              {roast.roaster_model && ` · ${roast.roaster_model}`}
              {roast.source_temp_unit && roast.source_temp_unit !== unit && (
                <> · source recorded °{roast.source_temp_unit}, shown in °{unit}</>
              )}
            </div>
          </div>
          <div className="right">
            <StarRating value={roast.rating?.stars ?? 0} size={22} />
            <div>
              <Link to={`/roasts/${encodeURIComponent(roast.roast_id)}/rate`}>
                {isRated ? "Edit rating" : "Rate"}
              </Link>
              {"  ·  "}
              <Link to={`/roasts/${encodeURIComponent(roast.roast_id)}/correct`}>
                Correct milestones
              </Link>
              {"  ·  "}
              <Link
                to={`/roasts/${encodeURIComponent(roast.roast_id)}/suggestions`}
              >
                Suggestions
              </Link>
            </div>
          </div>
        </div>
      </div>

      {evaluation ? (
        <VerdictCard ev={evaluation} roastId={roast.roast_id} />
      ) : evalErr ? (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Verdict</h2>
          <ErrorBox error={evalErr} />
        </div>
      ) : (
        <div className="card skeleton-card">
          <div className="skeleton skeleton-line" style={{ width: "30%" }} />
          <div className="skeleton skeleton-line" style={{ width: "70%", marginTop: 10 }} />
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Curves</h2>
        <RoastChart ts={ts} milestones={milestones} unit={unit} />
        <p className="muted small" style={{ marginBottom: 0 }}>
          Showing {ts.count} of the samples in [{formatTime(ts.start_s)}..
          {formatTime(ts.end_s)}] (cap {ts.max_points}; milestones are pinned).
          {ts.ror_source === "ibts" && " RoR is from IBTS — the bean-probe channel was not recorded."}
        </p>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Milestones</h2>
        <div className="milestones-layout">
          <div className="table-scroll">
            <MilestonesTable milestones={milestones} unit={unit} />
          </div>
          <MarkerProvenance
            roastId={roast.roast_id}
            discrepancies={discrepancies}
            lowConfidence={lowConfidence}
            error={provErr}
          />
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Recipe</h2>
        <RecipeCard recipe={roast.recipe ?? null} planned={roast.planned_recipe ?? null} />
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Derived metrics</h2>
        <DerivedMetricsTable m={roast.metrics} unit={unit} />
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Rating</h2>
        {isRated ? (
          <div className="col">
            <div>
              <StarRating value={roast.rating.stars} />{" "}
              <span className="muted small">
                rated {formatDate(roast.rating.rated_at)}
                {roast.rating.rated_via && ` via ${roast.rating.rated_via}`}
              </span>
            </div>
            {roast.rating.positive_notes.length > 0 && (
              <div>
                <span className="muted small">Positive: </span>
                {roast.rating.positive_notes.map((t) => (
                  <span className="badge ok" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            )}
            {roast.rating.negative_notes.length > 0 && (
              <div>
                <span className="muted small">Negative: </span>
                {roast.rating.negative_notes.map((t) => (
                  <span className="badge err" key={t}>
                    {t}
                  </span>
                ))}
              </div>
            )}
            {roast.rating.body && (
              <div>
                <span className="muted small">Body: </span>
                {roast.rating.body}
              </div>
            )}
            {roast.rating.brew_method && (
              <div>
                <span className="muted small">Brew: </span>
                {roast.rating.brew_method}
              </div>
            )}
            {roast.rating.notes && (
              <div className="small">“{roast.rating.notes}”</div>
            )}
          </div>
        ) : (
          <p className="muted">
            Not rated yet.{" "}
            <Link to={`/roasts/${encodeURIComponent(roast.roast_id)}/rate`}>
              Add a rating
            </Link>
            .
          </p>
        )}
      </div>

      <AIEvaluationCard roastId={roast.roast_id} />
    </div>
  );
}

// Effective value first, then the three pieces of evidence behind it.
// Provenance sits under the type, because "first crack 8:03" and "first
// crack 8:03 (recipe auto-stamp)" are different facts (issue #8).
function MilestonesTable({
  milestones,
  unit,
}: {
  milestones: DetectedMilestone[];
  unit: "C" | "F";
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>Milestone</th>
          <th>Effective</th>
          <th>Recorded</th>
          <th>Estimated</th>
          <th>Confidence</th>
          <th>Why</th>
        </tr>
      </thead>
      <tbody>
        {milestones.map((m) => {
          const cf = formatConfidence(m.effective_confidence);
          return (
            <tr key={m.milestone_type}>
              <td>
                <strong>{milestoneName(m.milestone_type)}</strong>
                <div className="small muted">{sourceLabel(m.effective_source)}</div>
              </td>
              <td className="mono">
                {formatTime(m.effective_time_s)} @{" "}
                {formatTemp(m.effective_temp, unit)}
                {m.effective_temp_source && (
                  <span className="muted small"> {m.effective_temp_source.toUpperCase()}</span>
                )}
              </td>
              <td className="mono">
                {formatTime(m.recorded_time_s)} @{" "}
                {formatTemp(m.recorded_temp, unit)}
                {m.recorded_time_s != null && (
                  <div className="small muted">{sourceLabel(m.recorded_source)}</div>
                )}
              </td>
              <td className="mono">
                {formatTime(m.estimated_time_s)}
                {m.estimated_uncertainty_s != null && (
                  <span className="muted small"> ±{Math.round(m.estimated_uncertainty_s)}s</span>
                )}{" "}
                @ {formatTemp(m.estimated_temp, unit)}
                {m.estimated_time_s != null && (
                  <div className="small muted">
                    detector {formatConfidence(m.detector_confidence).label}
                    {m.estimation_method && ` · ${m.estimation_method}`}
                  </div>
                )}
              </td>
              <td>
                <span className={`badge ${cf.cls}`}>{cf.label}</span>
              </td>
              <td className="small">
                {m.reason_codes.map((c) => (
                  <span className="badge" key={c}>
                    {c}
                  </span>
                ))}
                {m.correction_reason && (
                  <div className="muted">“{m.correction_reason}”</div>
                )}
                {m.observed_note && (
                  <div className="muted">noted: “{m.observed_note}”</div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// AI evaluation — calls the backend provider handler on demand.
//
// Kept inline (not a separate component file) because it's tightly
// coupled to a single roast and the surrounding card layout. Every call
// costs money and the operator should opt in; the deterministic verdict
// above is the primary answer.
function AIEvaluationCard({ roastId }: { roastId: string }) {
  const api = useApi();
  const [narrative, setNarrative] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ model: string; tokens: number | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const run = async () => {
    setLoading(true);
    setErr(null);
    try {
      const envl = await api.aiEvaluateRoast(roastId);
      setNarrative(envl.text);
      setMeta({ model: envl.data.model, tokens: envl.data.tokens_used });
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ marginTop: 0 }}>AI narrative</h2>
        <button onClick={run} disabled={loading}>
          {loading ? "Thinking…" : narrative ? "Re-evaluate" : "Narrate with AI"}
        </button>
      </div>
      <ErrorBox error={err} />
      {!narrative && !loading && !err && (
        <p className="muted small">
          Sends the verdict's grounding — metrics, milestones with
          provenance, plan, peers — to the configured model for a short
          narrative. Every number in the reply is checked against the
          grounding before it is shown. Costs about a cent per call.
        </p>
      )}
      {narrative && (
        <>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {narrative}
          </div>
          {meta && (
            <p className="muted small" style={{ marginTop: 8 }}>
              {meta.model}
              {meta.tokens != null && ` · ${meta.tokens} tokens`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Render the Recipe — the temperature-triggered plan the operator actually
// executed, reconstructed from the control trace (or the planned recipe
// from RoasTime when it is on file).
function RecipeCard({
  recipe,
  planned,
}: {
  recipe: Recipe | null;
  planned: Recipe | null;
}) {
  if (!recipe && !planned) {
    return (
      <p className="muted">
        No recipe on file and none could be derived (needs a charge
        milestone and at least ~20 post-charge samples).
      </p>
    );
  }
  return (
    <div className="col">
      {planned && <RecipeSteps recipe={planned} heading="Planned (from RoasTime)" />}
      {recipe && (
        <RecipeSteps
          recipe={recipe}
          heading={planned ? "Executed (derived from the trace)" : undefined}
        />
      )}
    </div>
  );
}

function RecipeSteps({ recipe, heading }: { recipe: Recipe; heading?: string }) {
  const source = String(recipe.source).replace(/_/g, " ");
  return (
    <div className="col" style={{ gap: 6 }}>
      {heading && <h3 style={{ margin: 0 }}>{heading}</h3>}
      <div className="small muted">
        {recipe.name && <><strong>{recipe.name}</strong>{"  ·  "}</>}
        preheat{" "}
        <span className="mono">
          {recipe.preheat_c != null ? `${recipe.preheat_c.toFixed(1)}°C` : "–"}
        </span>
        {"  ·  "}charge{" "}
        <span className="mono">{formatControlState(recipe.charge_controls)}</span>
        {"  ·  "}
        <span className="badge">{source}</span>
      </div>
      {recipe.steps.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No control changes or events between charge and drop.
        </p>
      ) : (
        <table className="compact">
          <thead>
            <tr>
              <th>Threshold</th>
              <th>Controls</th>
              <th>Event</th>
            </tr>
          </thead>
          <tbody>
            {recipe.steps.map((s, i) => (
              <tr key={`${i}-${s.threshold_c}-${s.event ?? "ctl"}`}>
                <td className="mono">
                  {s.threshold_c.toFixed(1)}°C
                  <span className="muted small"> {s.source.toUpperCase()}</span>
                </td>
                <td className="mono">{formatControlDelta(s.control)}</td>
                <td>
                  {s.event ? (
                    <span className="badge">{s.event.replace("_", " ")}</span>
                  ) : (
                    <span className="muted">–</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// "P8 F1 D9" — used for charge_controls where every knob is captured.
function formatControlState(c: ControlState): string {
  const parts: string[] = [];
  if (c.power != null) parts.push(`P${c.power}`);
  if (c.fan != null) parts.push(`F${c.fan}`);
  if (c.drum != null) parts.push(`D${c.drum}`);
  return parts.length ? parts.join(" ") : "–";
}

// Step control is a *delta*: any knob that didn't move is `null`. Render
// only the changed knobs so the operator can see the move at a glance.
function formatControlDelta(c: ControlState): string {
  const parts: string[] = [];
  if (c.power != null) parts.push(`P→${c.power}`);
  if (c.fan != null) parts.push(`F→${c.fan}`);
  if (c.drum != null) parts.push(`D→${c.drum}`);
  return parts.length ? parts.join("  ") : "–";
}

// Every temperature names its probe (issue #19): BT and IBTS are ~18 °C
// apart at the drop on this roaster.
function DerivedMetricsTable({ m, unit }: { m: DerivedMetrics; unit: "C" | "F" }) {
  const rows: [string, string][] = [
    ["total roast time", formatTime(m.total_roast_time_s)],
    ["charge IBTS", formatTemp(m.charge_temp_ibts, unit)],
    ["BT at charge", formatTemp(m.bt_at_charge, unit)],
    ["turning point BT", `${formatTemp(m.tp_bt_c, unit)} at ${formatTime(m.tp_time_s)}`],
    [`max RoR (${m.ror_probe.toUpperCase()})`, formatNumber(m.max_ror)],
    [`RoR at first crack (${m.ror_probe.toUpperCase()})`, formatNumber(m.ror_at_first_crack)],
    [`end RoR (${m.ror_probe.toUpperCase()})`, formatNumber(m.end_ror)],
    ["first crack IBTS / BT", `${formatTemp(m.fc_ibts_c, unit)} / ${formatTemp(m.fc_bt_c, unit)}`],
    ["drop IBTS / BT", `${formatTemp(m.drop_ibts_c, unit)} / ${formatTemp(m.drop_bt_c, unit)}`],
    ["drying", `${formatTime(m.drying_duration_s)} (${formatPercent(m.drying_pct == null ? null : m.drying_pct / 100)})`],
    ["maillard", `${formatTime(m.maillard_duration_s)} (${formatPercent(m.maillard_pct == null ? null : m.maillard_pct / 100)})`],
    ["development", `${formatTime(m.development_duration_s)} (${formatPercent(m.development_pct == null ? null : m.development_pct / 100)})`],
    ["weight loss", m.weight_loss_pct == null ? "–" : `${m.weight_loss_pct.toFixed(1)}%`],
    ["flick / crash / stall", [m.flick_detected && "flick", m.crash_detected && "crash", m.stall_detected && "stall"].filter(Boolean).join(", ") || "none detected"],
  ];
  return (
    <table>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ width: 240 }} className="muted">
              {k}
            </td>
            <td className="mono">{v}</td>
          </tr>
        ))}
        {m.phase_boundary_uncertainty_s != null && (
          <tr>
            <td className="muted">phase boundary uncertainty</td>
            <td className="mono">±{Math.round(m.phase_boundary_uncertainty_s)}s</td>
          </tr>
        )}
        {m.defect_reasons.length > 0 && (
          <tr>
            <td className="muted">defect reasons</td>
            <td className="small">
              {m.defect_reasons.map((r) => (
                <span className="badge" key={r}>
                  {r}
                </span>
              ))}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
